import { useState } from "react";
import { Crop as CropIcon, ImageOff, Palette } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useElementSize } from "@/hooks/useElementSize";
import { useImageAsset } from "@/hooks/useImageAsset";
import {
  cssFilter,
  effectiveFit,
  isQuarterTurn,
  placeImage,
} from "@/lib/image";
import type { ImageContent } from "@/types/content";

// Renders an image content window (TODO §3). Crop/transform/filters are applied at render
// time from stored data — nothing is re-encoded (§3.3). SVG is passed through untouched
// (§3.1); raster images get the placement + quarter-turn + CSS-filter pipeline (§3.6).
//
// The fit comes from `effectiveFit` and the geometry from `placeImage` — one rule shared with
// the export bake, so the editor cannot show a different crop from presentation, print, the
// PDF or the HTML file (B4).
export function ImageBlock({
  content,
  background,
  readOnly,
  onEdit,
}: {
  content: ImageContent;
  // §22.2 The window's paint, already resolved from the content's colour NAME by
  // `imageBackground`. Passed in rather than selected here: the parent already holds the
  // effective stylesheet, and this component renders in the print and off-screen thumbnail
  // trees where an extra subscription per image buys nothing.
  background: string;
  readOnly: boolean;
  onEdit: () => void;
}) {
  const url = useImageAsset(content.assetId);
  const [containerRef, { width: CW, height: CH }] =
    useElementSize<HTMLDivElement>();
  const [natural, setNatural] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const isSvg = content.mime === "image/svg+xml";
  const filter = cssFilter(content.filters);
  // One rule for every render path (B4): uncropped images are shown whole, a stored crop
  // fills the window. Presentation, print and the thumbnail capturer render this same
  // component, so they cannot disagree with the editor; the HTML export reads the same
  // helper when it bakes (`collectImageDataUrls`).
  const fit = effectiveFit(content);

  const rotation = content.transform?.rotation ?? 0;
  const quarter = isQuarterTurn(rotation);
  // Placement targets the pre-rotation box; a quarter turn swaps the container's dims.
  const preBox = quarter
    ? { width: CH, height: CW }
    : { width: CW, height: CH };
  const placement = natural
    ? placeImage(content.crop, natural, preBox, fit)
    : null;

  const flip = `scaleX(${content.transform?.flipH ? -1 : 1}) scaleY(${
    content.transform?.flipV ? -1 : 1
  })`;

  return (
    <div
      ref={containerRef}
      className="relative flex-1 overflow-hidden rounded-md border border-slate-200"
      // §22.2 The letterbox bars a `contain` fit leaves, and what shows through a transparent
      // PNG or SVG. Default white (see `imageBackground`), not the grey card this used to be.
      style={{ backgroundColor: background }}
    >
      {url == null ? (
        <div className="flex h-full w-full items-center justify-center text-slate-400">
          <ImageOff className="h-8 w-8" />
        </div>
      ) : isSvg ? (
        // Vector: no crop pipeline (raster edits don't apply to SVG), so `fit` here is
        // always `contain` — but it is read through the same helper rather than hardcoded,
        // so §22's user-set fit reaches vectors for free.
        <img
          src={url}
          alt=""
          className="h-full w-full"
          style={{ filter, objectFit: fit }}
        />
      ) : (
        // The stage is the pre-rotation box, centered and then rotated/flipped to fill
        // the container. Inside it, the crop rectangle's own box clips the image — so a
        // `contain` fit letterboxes without leaking the pixels the crop removed, and a
        // `cover` fit overflows the stage, which clips it.
        <div
          className="absolute left-1/2 top-1/2 overflow-hidden"
          style={{
            width: preBox.width,
            height: preBox.height,
            transform: `translate(-50%, -50%) rotate(${rotation}deg) ${flip}`,
            transformOrigin: "center",
          }}
        >
          <div
            className="absolute overflow-hidden"
            style={placement ? { ...placement.clip } : { visibility: "hidden" }}
          >
            {/* Kept mounted (hidden, not unmounted) before the first measurement so
                `onLoad` can report the natural size that the placement needs. */}
            <img
              src={url}
              alt=""
              onLoad={(e) =>
                setNatural({
                  width: e.currentTarget.naturalWidth,
                  height: e.currentTarget.naturalHeight,
                })
              }
              style={
                placement
                  ? { position: "absolute", ...placement.image, filter }
                  : { position: "absolute", visibility: "hidden" }
              }
            />
          </div>
        </div>
      )}

      {!readOnly && (
        // Offset from the corner so it clears the window's top-right Clear button.
        // §22.2 Shown for SVG as well, which has no crop pipeline but does have a background
        // and a filter — the dialog drops the controls that do not apply.
        <Button
          variant="secondary"
          size="sm"
          onClick={onEdit}
          className="absolute right-12 top-2 shadow"
        >
          {isSvg ? (
            <>
              <Palette className="mr-2 h-4 w-4" /> Background
            </>
          ) : (
            <>
              <CropIcon className="mr-2 h-4 w-4" /> Edit image
            </>
          )}
        </Button>
      )}
    </div>
  );
}
